import React, { useRef, useState, useEffect, useCallback, memo } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";

function NavBarInternal() {
    const location = useLocation();
    const isBeautyRoute = location.pathname === '/beauty';
    const { user, signOut } = useAuthenticator((context) => [context.user]);
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [isTreatmentsDropdownOpen, setIsTreatmentsDropdownOpen] = useState(false);
    const [isFaqDropdownOpen, setIsFaqDropdownOpen] = useState(false);
    
    // Add reference for treatments dropdown
    const treatmentsDropdownRef = useRef(null);
    const faqDropdownRef = useRef(null);

    const checkUserRole = useCallback(async () => {
      if (!user) return;
      try {
        const { tokens } = await fetchAuthSession();
        const groups = tokens?.accessToken?.payload?.["cognito:groups"] || [];

        if (groups.includes('Admin')) {
          setUserRole('Admin');
        } else if (groups.includes('HCPs')) {
          setUserRole('HCPs');
        } else {
          setUserRole('user');
        }
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    }, [user]);

    useEffect(() => {
      checkUserRole();
    }, [checkUserRole]);

    // Function to handle navigation
    const handleNavigation = useCallback((path) => {
        navigate(path);
        setIsOpen(false); // Close the menu after navigation
        setIsDropdownOpen(false);
        setIsTreatmentsDropdownOpen(false);
    }, [navigate]);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const handleClickOutside = useCallback((event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (treatmentsDropdownRef.current && !treatmentsDropdownRef.current.contains(event.target)) {
        setIsTreatmentsDropdownOpen(false);
      }
      if (faqDropdownRef.current && !faqDropdownRef.current.contains(event.target)) {
        setIsFaqDropdownOpen(false);
      }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }, [handleClickOutside]);

    const ProfileIcon = ({ onClick, isLoggedIn }) => (
        <div className="relative" ref={dropdownRef}>
            <div className="flex items-center gap-4">

        <button 
            onClick={() => isLoggedIn ? setIsDropdownOpen(!isDropdownOpen) : handleNavigation('/login')}
            className="flex items-center gap-2 text-gray-800 hover:text-teal-700 transition-colors duration-200"
            title={isLoggedIn ? "Profile" : "Login"}
        >
            <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke={isBeautyRoute ? 'gold' : 'currentColor'} 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="w-6 h-6"
            >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
            </svg>
        </button>
        </div>
        
        {isDropdownOpen && (
            <div className="absolute right-0 mt-3 w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-10">
            {isLoggedIn ? (
                <>
                <button
                    onClick={() => {
                        const dashboardPaths = {
                            'Admin': '/admin',
                            'HCPs': '/docdashboard',
                            'user': '/dashboard'
                        };
                        handleNavigation(dashboardPaths[userRole] || '/dashboard');
                        setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                    Dashboard
                </button>
                <button
                    onClick={() => {
                        handleNavigation(userRole === 'HCPs' ? '/doctor/messages' : '/messages');
                        setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                    Follow-ups
                </button>
                {userRole !== 'HCPs' && (
                  <button
                    onClick={() => {
                      handleNavigation('/history');
                      setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                  >
                    Treatment History
                  </button>
                )}
                {userRole !== 'HCPs' && (
                  <button
                    onClick={() => {
                      handleNavigation('/submitRequest');
                      setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                  >
                    Get Medication
                  </button>
                )}
                <button
                    onClick={() => {
                    handleNavigation('/profile');
                    setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                    Profile
                </button>
                <div className="border-t border-gray-100 my-1"></div>
                <button
                    onClick={() => {
                    signOut();
                    setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                >
                    Sign out
                </button>
                </>
            ) : (
                <>
                <button
                    onClick={() => {
                    handleNavigation('/submitRequest');
                    setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                    Get Medication
                </button>
                <button
                    onClick={() => {
                    handleNavigation('/');
                    setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                    Home
                </button>
                <button
                    onClick={() => {
                    handleNavigation('/about');
                    setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                    About
                </button>
                <button
                    onClick={() => {
                    handleNavigation('/contact');
                    setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                    Contact
                </button>
                <button
                    onClick={() => {
                    navigate('/#faq');
                    setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                    FAQ
                </button>
                </>
            )}
            </div>
        )}
        </div>
    );


    // Treatments dropdown component
    const TreatmentsDropdown = () => (
        <div className="relative" ref={treatmentsDropdownRef}>
            <button 
                onClick={() => setIsTreatmentsDropdownOpen(!isTreatmentsDropdownOpen)}
                className="flex items-center gap-1 text-lg font-heading leading-6"
            >
                Treatments
                <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className={`transition-transform ${isTreatmentsDropdownOpen ? 'rotate-180' : ''}`}
                >
                    <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
            </button>
            
            {isTreatmentsDropdownOpen && (
                <div className="absolute left-0 mt-3 w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-10">
                    <button
                        onClick={() => {
                            handleNavigation('/erectile-dysfunction');
                            setIsTreatmentsDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                    >
                        Erectile Dysfunction
                    </button>
                   
                </div>
            )}
        </div>
    );

    const FaqDropdown = () => (
        <div className="relative" ref={faqDropdownRef}>
            <button
                onClick={() => setIsFaqDropdownOpen(!isFaqDropdownOpen)}
                className="flex items-center gap-1 text-lg font-heading leading-6"
            >
                FAQ
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform ${isFaqDropdownOpen ? 'rotate-180' : ''}`}
                >
                    <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
            </button>

            {isFaqDropdownOpen && (
                <div className="absolute left-0 mt-3 w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-10">
                    <button
                        onClick={() => {
                            navigate('/#faq');
                            setIsFaqDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                    >
                        General FAQ
                    </button>
                    <button
                        onClick={() => {
                            navigate('/erectile-dysfunction#faq');
                            setIsFaqDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                    >
                        ED Medication FAQ
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div className={`NavBar shadow-md py-4 px-4 md:px-8 ${
            isBeautyRoute ? 'bg-black' : 'bg-white'
          }`}>
            {/* Update text colors */}
            <div className={`text-base leading-6 ${
              isBeautyRoute ? 'text-yellow-400' : 'text-gray-800'
            }`}>
                <div className="flex justify-between items-center max-w-7xl mx-auto px-4">
                    <div className="md:hidden">
                        {user ? (<></>): (
                            <>
                            <button onClick={toggleMenu} className="text-gray-800 focus:outline-none">
                            <svg className={`w-6 h-6 transition-transform duration-300 transform ${isOpen ? 'rotate-90' : 'rotate-0'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}></path>
                            </svg>
                            </button>
                            </>
                        )}
                    </div>
                    {/* Logo Section - Fixed width */}
                    <div className="w-48 absolute left-1/2 transform -translate-x-1/2 flex justify-center md:static md:transform-none md:justify-start">
                        <button 
                            onClick={() => handleNavigation('/')}
                            className="bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent font-mono cursor-pointer hover:opacity-80 transition-opacity text-2xl md:text-3xl font-extrabold tracking-tight"
                        >
                            OneScript
                        </button>
                    </div>

                    {/* Centered Nav Links */}
                    <div className="hidden md:flex items-center justify-center gap-8 md:gap-8">
                        {user ? (
                            <>
                               {/* Dashboard links for logged in users */}
                            </>
                        ) : (
                            <>
                                <button onClick={() => handleNavigation('/about')}
                                        className="text-lg font-heading leading-6">About
                                </button>
                                <TreatmentsDropdown />
                                <FaqDropdown />
                                <button onClick={() => handleNavigation('/contact')}
                                        className="text-lg font-heading leading-6">Contact
                                </button>
                            </>
                        )}
                    </div>

                    {/* Profile Section - Fixed width */}
                    <div className="w-48 flex gap-3 justify-end items-center">
                        {!user && (
                            <button
                                onClick={() => handleNavigation('/submitRequest')}
                                className={`hidden md:block group relative px-3 py-2 rounded-xl font-semibold text-sm md:text-base transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl ${
                                    isBeautyRoute 
                                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black hover:from-yellow-500 hover:to-yellow-600 shadow-yellow-400/30' 
                                        : 'bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800 shadow-teal-600/30'
                                }`}
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    Get Started
                                    <svg 
                                        width="16" 
                                        height="16" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                        className="transition-transform duration-300 group-hover:translate-x-1"
                                    >
                                        <polyline points="9,18 15,12 9,6"></polyline>
                                    </svg>
                                </span>
                                <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                                    isBeautyRoute 
                                        ? 'bg-gradient-to-r from-yellow-300 to-yellow-400' 
                                        : 'bg-gradient-to-r from-teal-500 to-teal-600'
                                }`}></div>
                            </button>
                        )}
                        <ProfileIcon 
                            onClick={user ? signOut : () => handleNavigation('/login')}
                            isLoggedIn={!!user}
                        />
                    </div>
                </div>
                <div 
                    className={`mt-4 md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
                        isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                    <div className="flex flex-col items-center py-2 bg-white rounded-lg shadow-md">
                        {user ? (
                            <>
                                {/* Mobile menu for logged in users */}
                            </>
                        ) : (
                            <>
                                <button onClick={() => handleNavigation('/submitRequest')}
                                        className="w-full py-4 mx-4 mb-2 text-white font-bold text-center bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95">
                                    <span className="flex items-center justify-center gap-2">
                                         Get Started
                                        <svg 
                                            width="16" 
                                            height="16" 
                                            viewBox="0 0 24 24" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            strokeWidth="2" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="9,18 15,12 9,6"></polyline>
                                        </svg>
                                    </span>
                                </button>
                                <button onClick={() => handleNavigation('/about')}
                                        className="w-full py-3 text-gray-800 font-medium text-center hover:bg-gray-50 transition-colors">About
                                </button>
                                <div className="w-full py-3 text-gray-900 font-bold text-center bg-gray-50">
                                    FAQ
                                </div>
                                <button onClick={() => { navigate('/#faq'); setIsOpen(false); }}
                                        className="w-full py-3 text-gray-800 font-medium text-center hover:bg-gray-50 transition-colors border-t border-gray-100">General FAQ
                                </button>
                                <button onClick={() => { navigate('/erectile-dysfunction#faq'); setIsOpen(false); }}
                                        className="w-full py-3 text-gray-800 font-medium text-center hover:bg-gray-50 transition-colors">ED Medication FAQ
                                </button>
                                <button onClick={() => handleNavigation('/contact')}
                                        className="w-full py-3 text-gray-800 font-medium text-center hover:bg-gray-50 transition-colors">Contact
                                </button>
                                <div className="w-full py-3 text-gray-900 font-bold text-center bg-gray-50">
                                    Treatments
                                </div>
                                <button onClick={() => handleNavigation('/erectile-dysfunction')}
                                        className="w-full py-3 text-gray-800 font-medium text-center hover:bg-gray-50 transition-colors border-t border-gray-100">Erectile Dysfunction
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const NavBar = memo(NavBarInternal);

export default NavBar;